import { createRoute, lazyRouteComponent, notFound } from '@tanstack/react-router';
import { z } from 'zod';
import { isApiError } from '@/api/errors';
import { labelFrom } from '@/app/breadcrumbs';
import { appRoute, type RouterContext } from '@/app/layout-routes';
import { requirePermissions } from '@/app/route-guards';
import { filterParam, listSearchSchema } from '@/lib/url-state';
import { gameQuery, gamesCatalogQuery, gamesSearchSchema, PLAYER_RTP_SORT_FIELDS } from './api';
import { GameNotFound } from './pages/game-not-found';

async function loadGame(context: RouterContext, rawId: string | number) {
  const id = Number(rawId);
  if (!Number.isInteger(id) || id <= 0) throw notFound();
  try {
    return await context.queryClient.ensureQueryData(gameQuery(id));
  } catch (error) {
    if (isApiError(error) && error.code === 'not_found') throw notFound();
    throw error;
  }
}

export const GAME_TABS = ['overview', 'settings', 'math', 'players'] as const;

/** Detail tabs, the analytics period, and the players table's paging and sort. */
const gameDetailSearchSchema = listSearchSchema({
  sortFields: PLAYER_RTP_SORT_FIELDS,
  filters: { tab: filterParam.enum(GAME_TABS), from: filterParam.date(), to: filterParam.date() },
});

const gameCreateSearchSchema = z.object({
  /** Create an RTP variant of this game: everything but name, RTP and version is copied. */
  variantOf: z.coerce.number().int().positive().optional().catch(undefined),
});

export const gamesListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'games',
  validateSearch: gamesSearchSchema,
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_LIST_GAMES'),
  loaderDeps: ({ search }) => ({ q: search.q, active: search.active }),
  // Non-blocking: grouping, the other filters and paging happen in the browser.
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(gamesCatalogQuery(deps));
  },
  component: lazyRouteComponent(() => import('./pages/games-list-page'), 'GamesListPage'),
});

export const gameCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'games/new',
  validateSearch: gameCreateSearchSchema,
  // A variant reads its source game, which needs the single-game permission too.
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_SINGLE_GAMES', 'UPDATE_GAMES'),
  loaderDeps: ({ search }) => ({ variantOf: search.variantOf }),
  loader: async ({ context, deps }) => {
    if (deps.variantOf !== undefined) await loadGame(context, deps.variantOf);
  },
  staticData: { crumbs: () => [{ label: 'New game' }] },
  notFoundComponent: GameNotFound,
  component: lazyRouteComponent(() => import('./pages/game-create-page'), 'GameCreatePage'),
});

export const gameDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'games/$gameId',
  validateSearch: gameDetailSearchSchema,
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_SINGLE_GAMES'),
  loader: ({ context, params }) => loadGame(context, params.gameId),
  staticData: { crumbs: (params, game) => [{ label: labelFrom(game, 'Name') ?? `Game ${params.gameId ?? ''}` }] },
  notFoundComponent: GameNotFound,
  component: lazyRouteComponent(() => import('./pages/game-detail-page'), 'GameDetailPage'),
});

export const gameEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'games/$gameId/edit',
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_SINGLE_GAMES', 'UPDATE_GAMES'),
  loader: ({ context, params }) => loadGame(context, params.gameId),
  staticData: {
    crumbs: (params, game) => [
      { label: labelFrom(game, 'Name') ?? `Game ${params.gameId ?? ''}`, href: `/games/${params.gameId ?? ''}` },
      { label: 'Edit' },
    ],
  },
  notFoundComponent: GameNotFound,
  component: lazyRouteComponent(() => import('./pages/game-edit-page'), 'GameEditPage'),
});

/** Routes for this feature, attached to `appRoute`; `app/routeTree.ts` spreads them in. */
export const gamesRoutes = [gamesListRoute, gameCreateRoute, gameDetailRoute, gameEditRoute] as const;
